import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import EventCard from '../components/Events/EventCard'; 
import EventCreationForm from '../components/Events/EventCreationForm';
import { server } from "../server";
import Sidebar from '../components/Layout/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MyEvents from '../components/Events/MyEvents';
import LocationTracker from '../components/Location/Locator';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import SharedRightSlideout from '../components/Layout/SharedRightSlideout';

const EVENT_CATEGORY_DEFINITIONS = [
    {
        id: 'music',
        title: 'Music Events',
        keywords: ['music', 'concert', 'dj', 'band', 'live', 'karaoke', 'open mic', 'album', 'song', 'festival'],
    },
    {
        id: 'activity',
        title: 'Activity',
        keywords: ['activity', 'class', 'workout', 'yoga', 'fitness', 'game', 'sports', 'hike', 'run', 'ride', 'tournament'],
    },
    {
        id: 'community',
        title: 'Community',
        keywords: ['community', 'market', 'vendor', 'shop', 'popup', 'network', 'meetup', 'workshop', 'social', 'fundraiser'],
    },
    {
        id: 'other',
        title: 'Other Events',
        keywords: [],
    },
];

const getEventSearchText = (event) => [
    event.title,
    event.description,
    event.location?.address,
].filter(Boolean).join(' ').toLowerCase();

const getEventCategoryId = (event) => {
    const eventSearchText = getEventSearchText(event);
    const matchedCategory = EVENT_CATEGORY_DEFINITIONS.find((category) => (
        category.keywords.length > 0 && category.keywords.some((keyword) => eventSearchText.includes(keyword))
    ));

    return matchedCategory?.id || 'other';
};

const parseEventDateTime = (dateValue, timeValue, fallbackToEndOfDay = false) => {
    const eventDate = new Date(dateValue);

    if (Number.isNaN(eventDate.getTime())) {
        return null;
    }

    if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
        const [hours, minutes] = timeValue.split(':').map(Number);
        eventDate.setHours(hours, minutes, 0, 0);
    } else if (fallbackToEndOfDay) {
        eventDate.setHours(23, 59, 59, 999);
    } else {
        eventDate.setHours(0, 0, 0, 0);
    }

    return eventDate;
};

const getEventTiming = (event) => {
    const startAt = parseEventDateTime(event.date, event.startTime);
    const endAt = parseEventDateTime(event.endDate || event.date, event.endTime, true) || startAt;
    return { startAt, endAt };
};

const scrollEventRail = (railId, direction) => {
    if (typeof document === 'undefined') {
        return;
    }

    const rail = document.getElementById(railId);
    if (!rail) {
        return;
    }

    const scrollAmount = Math.max(rail.clientWidth * 0.82, 220);
    rail.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
};

const EventGalleryRail = ({ railId, children, listClassName = '' }) => {
    const hasMultipleItems = React.Children.count(children) > 1;

    return (
    <div className={`events-page-gallery-rail-shell${hasMultipleItems ? '' : ' events-page-gallery-rail-shell--single'}`}>
        {hasMultipleItems && (
            <button
                type="button"
                className="events-page-gallery-arrow events-page-gallery-arrow--prev"
                aria-label="Scroll events left"
                onClick={() => scrollEventRail(railId, -1)}
            >
                <FaChevronLeft aria-hidden="true" />
            </button>
        )}
        <div
            id={railId}
            className={`events-page-events-list events-page-events-list--compact ${listClassName}`.trim()}
            tabIndex="0"
        >
            {children}
        </div>
        {hasMultipleItems && (
            <button
                type="button"
                className="events-page-gallery-arrow events-page-gallery-arrow--next"
                aria-label="Scroll events right"
                onClick={() => scrollEventRail(railId, 1)}
            >
                <FaChevronRight aria-hidden="true" />
            </button>
        )}
    </div>
    );
};

const EventsPage = () => {
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [filterDistance, setFilterDistance] = useState('all');
    const [filterTime, setFilterTime] = useState('all');
    const [open, setOpen] = useState(false);
    const [userLocation, setUserLocation] = useState(null);

    useEffect(() => {
        const savedLocation = sessionStorage.getItem('userLocation');
        if (savedLocation) {
            setUserLocation(JSON.parse(savedLocation));
        }
    }, []);

    const handleLocationRetrieved = (location) => {
        setUserLocation(location);
    };

    const fetchEvents = useCallback(async () => {
        try {
            const response = await axios.get(`${server}/event/events`);
            const now = new Date();
            const fetchedEvents = response.data
                .map((event) => {
                    const { startAt, endAt } = getEventTiming(event);
                    const hasCoordinates = Number.isFinite(event?.location?.lat) && Number.isFinite(event?.location?.lng);

                    return {
                        ...event,
                        startAt,
                        endAt,
                        distance: userLocation && hasCoordinates
                            ? getDistanceFromLatLonInKm(
                                userLocation.latitude,
                                userLocation.longitude,
                                event.location.lat,
                                event.location.lng
                              )
                            : null,
                        timeToEvent: startAt ? (startAt - now) / 36e5 : null,
                    };
                })
                .filter((event) => !event.endAt || event.endAt >= now);

            const filtered = fetchedEvents.filter(event =>
                (filterDistance === 'all' || (typeof event.distance === 'number' && event.distance <= filterDistance)) &&
                (filterTime === 'all' || (typeof event.timeToEvent === 'number' && event.timeToEvent >= 0 && event.timeToEvent <= filterTime))
            ).sort((a, b) => {
                if (a.startAt && b.startAt && a.startAt.getTime() !== b.startAt.getTime()) {
                    return a.startAt - b.startAt;
                }

                if (typeof a.distance === 'number' && typeof b.distance === 'number') {
                    return a.distance - b.distance;
                }

                return 0;
            });

            setFilteredEvents(filtered);
        } catch (error) {
            console.error('Failed to fetch events:', error);
            toast.error('Failed to load events.');
        }
    }, [filterDistance, filterTime, userLocation]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]); 

    const handleRSVP = (eventId) => {
        setFilteredEvents((prevEvents) => prevEvents.map((event) =>
            event._id === eventId ? { ...event, isGoing: true } : event
        ));
        toast.success('RSVP successful!');
    };

    const handleCancelRSVP = (eventId) => {
        setFilteredEvents((prevEvents) => prevEvents.map((event) =>
            event._id === eventId ? { ...event, isGoing: false } : event
        ));
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
        const R = 6371; // Radius of the earth in km
        const dLat = deg2rad(lat2-lat1);
        const dLon = deg2rad(lon2-lon1);
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
            Math.sin(dLon/2) * Math.sin(dLon/2)
            ; 
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        const distance = R * c; // Distance in km
        return distance;
    }

    function deg2rad(deg) {
        return deg * (Math.PI/180);
    }

    const handleFilterChange = (e, type) => {
        const value = e.target.value;
        if (type === 'distance') {
            setFilterDistance(value === 'all' ? 'all' : Number(value));
        } else if (type === 'time') {
            setFilterTime(value === 'all' ? 'all' : Number(value));
        }
    };

    const featuredEvent = filteredEvents[0];
    const recommendedEvents = filteredEvents.slice(0, 6);
    const categorizedEvents = EVENT_CATEGORY_DEFINITIONS.map((category) => ({
        ...category,
        events: filteredEvents.filter((event) => getEventCategoryId(event) === category.id),
    })).filter((category) => category.events.length > 0);

    return (
        <div className="events-page-container">
            
            <Sidebar className="events-page-sidebar" />
            <main className="events-page-main-content">
                <h2 className="explore-title">Events</h2>
                <div className="events-page-top-controls">
                    <div className="events-page-controls-cluster">
                        <LocationTracker onLocationRetrieved={handleLocationRetrieved} />
                        <button onClick={handleOpen} className="events-page-create-event-button">
                            Create Event
                        </button>
                        <section className="events-page-filters">
                            <select
                                value={filterDistance}
                                onChange={(e) => handleFilterChange(e, 'distance')}
                                className="events-page-select"
                                aria-label="Filter by Distance"
                            >
                                <option value="all">All Distances</option>
                                <option value="25">25 km</option>
                                <option value="50">50 km</option>
                                <option value="75">75 km</option>
                                <option value="100">100 km</option>
                                <option value="125">125 km</option>
                                <option value="150">150 km</option>
                            </select>
                            <select
                                value={filterTime}
                                onChange={(e) => handleFilterChange(e, 'time')}
                                className="events-page-select"
                                aria-label="Filter by Time"
                            >
                                <option value="all">All Times</option>
                                <option value="24">Next 24 hours</option>
                                <option value="48">Next 48 hours</option>
                                <option value="72">Next 72 hours</option>
                                <option value="168">Next 7 days</option>
                            </select>
                        </section>
                    </div>
                </div>

                {filteredEvents.length > 0 ? (
                    <div className="events-page-layout">
                        <section className="events-page-primary-column" aria-label="Event galleries">
                            {featuredEvent && (
                                <section className="events-page-featured-widget" aria-label="Featured event">
                                    <div className="events-page-section-heading-row">
                                        <h3 className="events-page-section-title">Featured Events</h3>
                                    </div>
                                    <div className="events-page-featured-card-shell">
                                        <EventCard event={featuredEvent} onRSVP={handleRSVP} onCancelRSVP={handleCancelRSVP} />
                                    </div>
                                </section>
                            )}

                            <section className="events-page-category-stack" aria-label="Event categories">
                                {categorizedEvents.map((category) => (
                                    <div className="events-page-category-section" key={category.id}>
                                        <div className="events-page-section-heading-row">
                                            <h3 className="events-page-section-title">{category.title}</h3>
                                            <span className="events-page-section-count">{category.events.length}</span>
                                        </div>
                                        <EventGalleryRail railId={`events-page-${category.id}-rail`}>
                                            {category.events.map((event) => (
                                                <EventCard key={event._id} event={event} onRSVP={handleRSVP} onCancelRSVP={handleCancelRSVP} />
                                            ))}
                                        </EventGalleryRail>
                                    </div>
                                ))}
                            </section>
                        </section>

                        <aside className="events-page-aside events-page-aside--widget" aria-label="Your events and recommendations">
                            <section className="events-page-side-widget events-page-side-widget--mine">
                                <MyEvents title="Your Events" />
                            </section>
                            <section className="events-page-side-widget events-page-side-widget--recommended">
                                <div className="events-page-section-heading-row">
                                    <h3 className="events-page-section-title">Recommended</h3>
                                </div>
                                <EventGalleryRail railId="events-page-recommended-rail" listClassName="events-page-recommended-slideshow">
                                    {recommendedEvents.map((event) => (
                                        <div className="events-page-recommended-slide" key={event._id}>
                                            <EventCard event={event} onRSVP={handleRSVP} onCancelRSVP={handleCancelRSVP} />
                                        </div>
                                    ))}
                                </EventGalleryRail>
                            </section>
                        </aside>
                    </div>
                ) : (
                    <p className="events-page-empty-state">No events found matching your criteria.</p>
                )}
            </main>
            <SharedRightSlideout
                isOpen={open}
                onClose={handleClose}
                title="Create Event"
                subtitle="Add the details, image, date, and location."
                className="events-create-shared-slideout"
                bodyClassName="events-create-shared-slideout-body"
            >
                <EventCreationForm onClose={handleClose} onCreated={fetchEvents} />
            </SharedRightSlideout>
            <ToastContainer position="top-right" autoClose={5000} />
        </div>
    );
};

export default EventsPage;



